import {RFValue} from 'react-native-responsive-fontsize';
import {colors} from './colors';
import {Dimensions, Platform} from 'react-native';
import {getByScreenHeight, getByScreenSize, isWeb} from '../utils/responsive';

const height = Dimensions.get('screen').height;
const width = Dimensions.get('screen').width;

/**
 * The reference height for calculating responsive font sizes based on screen size.
 * @type {number}
 */
export const SCREEN_HEIGHT_REFERENCE =
  height *
  (isWeb
    ? getByScreenHeight(2.1, getByScreenSize(1, 1.4))
    : getByScreenSize(Platform.OS === 'android' ? 0.95 : 1, 1.2));

export const WEB_FONT_SIZES = {
  s1: 44,
  s2: 38,
  s3: 32,
  s4: 27,
  s5: 26,
  s55: 24,
  s58: 22.5,
  s6: 21,
  s7: 19,
  s8: 18,
  s85: 17.5,
  s89: 17.2,
  s9: 17,
  s10: 15,
  s11: 14,
  s12: 12,
};

// Helper function to get font size based on platform
export const getFontSize = (size: number) => {
  return RFValue(size, SCREEN_HEIGHT_REFERENCE);
};

// Helper function to get font names based on platform
const getFontConfig = () => {
  if (Platform.OS === 'web') {
    // Use font-family for web
    return {
      ar: {
        fontName: 'BBC Reith Sans',
        regular: 'BBC Reith Sans',
        semi_bold: 'BBC Reith Sans',
        bold: 'BBC Reith Sans',
      },
      en: {
        fontName: 'BBC Reith Sans',
        regular: 'BBC Reith Sans',
        semi_bold: 'BBC Reith Sans',
        bold: 'BBC Reith Sans',
      },
    };
  } else {
    // Use font files for native
    return {
      ar: {
        fontName: 'BBC Reith Sans',
        regular: 'BBCReithSans-Regular',
        semi_bold: 'BBCReithSans-Medium',
        bold: 'BBCReithSans-Bold',
      },
      en: {
        fontName: 'BBC Reith Sans',
        regular: 'BBCReithSans-Regular',
        semi_bold: 'BBCReithSans-Medium',
        bold: 'BBCReithSans-Bold',
      },
    };
  }
};

/**
 * Represents the light theme object.
 * This can be used to create different themes like dark and light.
 * It can be then connected to React context and controlled via a setting flag.
 * @type {object}
 */
const lightTheme = {
  primary: '#6633FF', // Vibrant purple/blue from image
  secondary: '#5A20CB', // Darker purple for secondary elements
  tertiary: '#4BB2C0',
  ripple: '#D9D9D9',
  overlay: 'rgba(0,0,0,0.3)',
  headerBackground: '#FFFFFF',
  homeBackground: '#F8F8FF', // Very light blue/off-white from image
  screenBackground: '#FFFFFF',
  cardBackground: '#FFFFFF', // Pure white from image
  cardBackgroundEnd: 'rgba(249,248,255,0.95)',
  icon: '#6633FF', // Purple/blue for icons
  skeletonBackground: '#cccccc',
  skeletonShimmer: '#FFFFFF',
  border: '#D9D9D9',
  primaryOverlay: 'rgba(102, 51, 255, 0.3)', // Updated to match new primary
  backgroundOverlay: 'rgba(255,255,255,0.1)',
  imageBackground: '#F8F8FF', // Light blue background
  alert: {
    successBackgroundPrimary: '#03A65A',
    errorBackgroundPrimary: '#F63E50',
    warningBackgroundPrimary: '#F88F01',
    helpBackgroundPrimary: '#739BE5',
    successBackgroundSecondary: '#005E38',
    errorBackgroundSecondary: '#99004D',
    warningBackgroundSecondary: '#B15500',
    helpBackgroundSecondary: '#3F62A1',
    textPrimary: '#FFFFFF',
  },
  fonts: getFontConfig(),
  text: {
    dark: '#333333', // Dark gray/black from image
    black: '#000000',
    border: '#CCCCCC',
    icon: '#333333', // Dark gray for icons
    error: '#E41438',
    success: '#1D832E',
    grey: '#707070',
    greyLight: '#DEDBDB',
    title: '#000000',
    description: '#707070',
    light: '#ffffff', // Pure white from image
    s1: getFontSize(WEB_FONT_SIZES.s1),
    s2: getFontSize(WEB_FONT_SIZES.s2),
    s3: getFontSize(WEB_FONT_SIZES.s3),
    s4: getFontSize(WEB_FONT_SIZES.s4),
    s5: getFontSize(WEB_FONT_SIZES.s5),
    s55: getFontSize(WEB_FONT_SIZES.s55),
    s58: getFontSize(WEB_FONT_SIZES.s58),
    s6: getFontSize(WEB_FONT_SIZES.s6),
    s7: getFontSize(WEB_FONT_SIZES.s7),
    s8: getFontSize(WEB_FONT_SIZES.s8),
    s85: getFontSize(WEB_FONT_SIZES.s85),
    s89: getFontSize(WEB_FONT_SIZES.s89),
    s9: getFontSize(WEB_FONT_SIZES.s9),
    s10: getFontSize(WEB_FONT_SIZES.s10),
    s11: getFontSize(WEB_FONT_SIZES.s11),
    s12: getFontSize(WEB_FONT_SIZES.s12),
  },
  button: {
    text: '#FFFFFF',
    lightColor: '#FFFFFF',
    outline: colors.primary,
  },
  textInput: {
    text: '#070A7A',
    cursorColor: colors.primary,
    placeholder: '#777777',
    border: '#CCCCCC',
    required: '#ffd2d2',
    background: '#FFFFFF',
  },
  fab: {
    backgroundColor: '#F5F6F7',
  },
  header: {
    primary: colors.secondary,
    background: '#FFFFFF',
    startLinearGradient: 'rgba(255,255,255,0.95)',
    endLinearGradient: 'rgba(255,255,255,0.95)',
  },
  bottomTabs: {
    background: 'rgba(255,255,255,0.95)',
    activeTabBackground: colors.secondary + 'EE',
    label: '#FFFFFF',
    icon: '#a1bcfb',
    iconFocus: '#2D27F6',
  },
  activityListItem: {
    startColor: '#E5E5E5',
    endColor: '#f1f1f1',
  },
  switch: {
    trackOff: '#C8D8FF',
    trackOn: '#C8D8FF',
    thumbOff: '#9ab3ef',
    thumbOn: '#2D27F6',
  },
  search: {
    placeholder: '#181818',
  },
  questionInput: {
    text: '#ffffff',
    placeholder: '#D9D9D9',
    startColor: 'rgba(9,7,71,0.7)',
    endColor: 'rgba(42,44,112,0.4)',
  },
  voiceQuestionInput: {
    text: '#333333', // Dark gray from image
    background: '#FFFFFF', // Pure white from image
    inputPlaceholder: '#AAAAAA', // Light gray placeholder
    iosEndColor: 'rgba(0,0,0,0)',
    mainButtonStartColor: '#6633FF', // Purple/blue from image
    mainButtonEndColor: '#5A20CB', // Darker purple
    bubbleBackground: '#FFFFFF', // White background for bubbles
  },
  afterLogin: {
    contentBackground: '#f5f5f5',
    text: '#181818',
    label: '#5d5d5d',
    line: '#999',
    selected: 'rgba(0,0,0,0.1)',
    checkIcon: '#e7e6e6',
    checkIconBackground: colors.primary,
  },
  chatBubble: {
    backgroundSend: '#6633FF', // Purple/blue for user messages from image
    backgroundReceived: '#FFFFFF', // White for assistant messages from image
    selected: 'rgba(102, 51, 255, 0.2)', // Purple overlay
    date: '#333333', // Dark gray for timestamps
    error: '#ffdfdf',
    favorite: '#FFD707',
    sendText: '#FFFFFF', // White text on purple background
    receivedText: '#333333', // Dark gray text on white background
    online: '#52bd57',
    offline: '#9E9E9E',
  },
  onboarding: {
    text: '#FFFFFF',
    background: '#0B0D6F',
    textSecondary: colors.tertiary,
  },
  home: {
    cardBackground: '#C8D8FF',
  },
  menu: {
    nav: '#0B0D6F',
  },
  fontChange: {
    card: '#C8D8FF',
  },
  settings: {
    iconBackground: '#EEF0FF',
    icon: '#2823F5',
  },
  quickAccess: {
    background: '#FFFFFF',
    iconBackground: '#DEE8FF',
  },
  chat: {
    startLinearGradient: '#BBFDFD',
    endLinearGradient: '#8F85F5',
  },
  tabNews: {
    startLinearGradient: '#76fefb',
    secondLinearGradient: '#6b75ff',
    thirdLinearGradient: '#3a00b7',
    button: '#27B9E0',
    cardBackground: '#C8D8FF',
  },
  drugsDiseases: {
    tabBackground: '#27B9E0',
    gradientTopLeft: '#77FEFF',
    gradientMidTop: '#6492F0',
    gradientCenter: '#565BE3',
    gradientBottomRight: '#3F00CC',
  },
  questionModal: {
    title: '#2823F5',
  },
  patients: {
    headerBackground: '#e0e9ff',
  },
  // Additional theme properties for enhanced chat functionality
  isMobile: false, // Will be set dynamically
  shadow: 'rgba(0,0,0,0.1)',
  optionIcon: '#707070',
  shine: '#6633FF',
  categoryBullet: {
    background: '#F5F5F5',
    selectedBackground: '#6633FF',
    text: '#333333',
    selectedText: '#FFFFFF',
    selectedBorder: '#6633FF', // Fixed property name
    hoverBackground: '#E8E8E8',
    selectedHoverBackground: '#5A20CB',
  },
  dropdown: {
    background: '#FFFFFF',
    text: '#333333',
    delete: '#E41438',
  },
  table: {
    thead: '#F8F8FF',
    th: '#333333',
    evenRow: '#F8F8FF',
    trHover: '#F0F0F0',
    text: '#333333',
  },
  scrollbar: {
    track: '#F0F0F0',
    thumb: '#CCCCCC',
    thumbHover: '#AAAAAA',
  },
};

/**
 * The type representing the structure of the light theme.
 * @type {Theme}
 */
type Theme = typeof lightTheme;

export type {Theme};
export {getFontConfig};
export default lightTheme;
